import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiExternalLink } from 'react-icons/fi';
import { FaApple, FaAndroid, FaBell, FaMobileAlt } from 'react-icons/fa';
import Navbar from '../../Components/Navbar/Navbar';
import Footer from '../../Components/Footer/Footer';
import SEO from '../../Components/SEO/SEO';
import { SETUP_APP_META } from '../../seo/pageMeta';
import styles from './SetupApp.module.css';

const PORTAL_URL = 'https://portal.stylon.co';

const ANDROID_STEPS = [1, 2, 3, 4];
const IPHONE_STEPS = [1, 2, 3, 4];
const NOTIF_STEPS = [1, 2, 3];

const SetupApp = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar' || i18n.language === 'he';

  return (
    <div className={styles.page}>
      <SEO {...SETUP_APP_META} />
      <Navbar />

      {/* Hero */}
      <section className={styles.hero}>
        <div className="container">
          <div className={styles.heroBadge}>
            <FaMobileAlt className={styles.badgeIcon} />
            {t('setup.badge')}
          </div>
          <h1 data-aos="fade-up">{t('setup.title')}</h1>
          <p data-aos="fade-up" data-aos-delay="100">{t('setup.subtitle')}</p>
          <a
            href={PORTAL_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.heroCta}
            data-aos="fade-up"
            data-aos-delay="200"
          >
            
            {t('setup.openPortalCta')}
            <FiExternalLink className={styles.extIcon} />
          </a>
        </div>
      </section>

      {/* Steps */}
      <section className={styles.stepsSection}>
        <div className="container">
          <div className={styles.stepsContainer}>

            {/* Step 1: open the portal */}
            <div className={styles.stepCard} data-aos="fade-up">
              <div className={styles.stepHeader}>
                <span className={styles.stepBadge}>1</span>
                <h2 className={styles.sectionTitle}>{t('setup.openPortalTitle')}</h2>
              </div>
              <p className={styles.sectionBody}>{t('setup.openPortalBody')}</p>
              <a
                href={PORTAL_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.portalLink}
              >
                portal.stylon.co <FiExternalLink className={styles.externalIcon} />
              </a>
            </div>

            {/* iPhone */}
            <div className={styles.stepCard} data-aos="fade-up" data-aos-delay="80">
              <div className={styles.stepHeader}>
                <span className={styles.stepBadge}>2</span>
                <h2 className={styles.sectionTitle}>
                  <FaApple className={styles.platformIcon} /> {t('setup.iphoneTitle')}
                </h2>
              </div>
              <p className={styles.sectionBody}>{t('setup.iphoneBody')}</p>
              <ol className={styles.stepList} dir={isRtl ? 'rtl' : 'ltr'}>
                {IPHONE_STEPS.map((n) => (
                  <li key={n} className={styles.stepItem}>
                    <span className={styles.stepNum}>{n}</span>
                    <span>{t(`setup.iphoneStep${n}`)}</span>
                  </li>
                ))}
              </ol>
              <div className={styles.tipBox}>
                <p className={styles.tipText}>{t('setup.iphoneTip')}</p>
              </div>
            </div>

            {/* Android */}
            <div className={styles.stepCard} data-aos="fade-up" data-aos-delay="160">
              <div className={styles.stepHeader}>
                <span className={styles.stepBadge}>3</span>
                <h2 className={styles.sectionTitle}>
                  <FaAndroid className={styles.platformIcon} style={{ color: '#3ddc84' }} /> {t('setup.androidTitle')}
                </h2>
              </div>
              <p className={styles.sectionBody}>{t('setup.androidBody')}</p>
              <ol className={styles.stepList} dir={isRtl ? 'rtl' : 'ltr'}>
                {ANDROID_STEPS.map((n) => (
                  <li key={n} className={styles.stepItem}>
                    <span className={styles.stepNum}>{n}</span>
                    <span>{t(`setup.androidStep${n}`)}</span>
                  </li>
                ))}
              </ol>
              <div className={styles.tipBox}>
                <p className={styles.tipText}>{t('setup.androidTip')}</p>
              </div>
            </div>

            {/* Enable notifications */}
            <div className={styles.stepCard} data-aos="fade-up" data-aos-delay="240">
              <div className={styles.stepHeader}>
                <span className={styles.stepBadge}>4</span>
                <h2 className={styles.sectionTitle}>
                  <FaBell className={styles.platformIcon} style={{ color: '#FE4E00' }} /> {t('setup.notifTitle')}
                </h2>
              </div>
              <p className={styles.sectionBody}>{t('setup.notifBody')}</p>
              <ol className={styles.stepList} dir={isRtl ? 'rtl' : 'ltr'}>
                {NOTIF_STEPS.map((n) => (
                  <li key={n} className={styles.stepItem}>
                    <span className={styles.stepNum}>{n}</span>
                    <span>{t(`setup.notifStep${n}`)}</span>
                  </li>
                ))}
              </ol>
              <div className={styles.infoBox}>
                <p className={styles.infoText}>{t('setup.notifNote')}</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* CTA */}
      <section className={styles.ctaSection}>
        <div className="container">
          <h2>{t('setup.ctaTitle')}</h2>
          <p>{t('setup.ctaBody')}</p>
          <Link to="/features/app" className={styles.ctaLearnMore}>
            {t('setup.ctaLearnMore')}
          </Link>
          <a
            href={PORTAL_URL}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.ctaButton}
          >
            
            {t('setup.openPortalCta')}
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SetupApp;
